import { get } from 'lodash';

import { FD_NAME_SLUG } from 'src/constants';
import { getFirstNestedWithSaneDefault } from 'src/helpers/common';

/** Shape of a single metadata field from the MetaQ GraphQL response */
interface MetadataFieldEntry {
  definition?: { slug?: string; name?: string };
  values?: Array<{ data?: { data?: string } }>;
}

/** Options for resolving a display name for an asset */
export interface AssetDisplayNameOptions {
  /** Explicit name (e.g. from enriched drag data or item.name) */
  name?: string | null;
  /** Name from GraphQL contentObject.name */
  contentObjectName?: string | null;
  /** Raw metadata payload from useMetaQ */
  metadataPayload?: Record<string, unknown> | null;
  /** Filename from format (with or without extension) */
  filename?: string | null;
  /** Asset ID for last-resort display */
  id?: string | null;
}

const TITLE_LIKE_PATTERNS = ['title', 'name', 'subject', 'headline', 'label'];
const NON_TITLE_PATTERNS = [
  'id',
  'path',
  'url',
  'hash',
  'encoding',
  'mimetype',
  'format',
  'mediasize',
  'mediatype',
  'timecode',
  'inpoint',
  'outpoint',
  'duration',
  'created_at',
  'last_modified',
  'media_group',
  'language',
];
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Broader pattern catching alphanumeric UUID-like IDs (e.g. Elasticsearch document IDs) */
const ID_LIKE_PATTERN = /^[0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{8,}$/i;
const MIN_SCORE_THRESHOLD = 5;
const MAX_DISPLAY_LENGTH = 60;

/**
 * Extract the string value from a metadata field entry.
 */
function getFieldValue(field: MetadataFieldEntry): string {
  return (
    field.values
      ?.map((val) => get(val, 'data.data', ''))
      .filter(Boolean)
      .join(', ') ?? ''
  );
}

/**
 * Score a metadata field by how likely it is to be a good display title.
 * Higher score = better candidate. Returns the score and the extracted value.
 */
function scoreField(field: MetadataFieldEntry): { score: number; value: string } {
  const value = getFieldValue(field);
  const slug = (field.definition?.slug ?? '').toLowerCase();
  const name = (field.definition?.name ?? '').toLowerCase();
  const identifier = `${slug} ${name}`;

  let score = 0;

  // Empty/whitespace — disqualify
  if (!value || !value.trim()) {
    return { score: -10, value };
  }

  // Boost: slug/name contains title-like words
  if (TITLE_LIKE_PATTERNS.some((p) => identifier.includes(p))) {
    score += 10;
  }

  // Penalize: slug/name matches known non-title fields
  if (NON_TITLE_PATTERNS.some((p) => slug === p || name === p)) {
    score -= 5;
  }

  // Boost: non-empty text value
  score += 5;

  // Boost: short value (titles are usually short)
  if (value.length < 80) {
    score += 3;
  }

  // Boost: contains spaces (natural language, not a code/path)
  if (value.includes(' ')) {
    score += 2;
  }

  // Penalize: looks like a URL
  if (value.startsWith('http://') || value.startsWith('https://')) {
    score -= 5;
  }

  // Penalize: looks like a file path
  if (value.includes('/') || value.includes('\\')) {
    score -= 5;
  }

  // Penalize: looks like a UUID
  if (UUID_PATTERN.test(value)) {
    score -= 5;
  }

  // Penalize: purely numeric
  if (/^\d+$/.test(value.trim())) {
    score -= 3;
  }

  return { score, value };
}

/**
 * Attempt to infer a display title from all available metadata fields.
 * Scores each field and returns the best candidate if it meets the threshold.
 * This is the last-resort smart fallback before falling back to ID or "Untitled Asset".
 */
export function inferTitleFromMetadata(
  metadataPayload: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!metadataPayload) return undefined;

  const fields = getFirstNestedWithSaneDefault<MetadataFieldEntry[]>(
    metadataPayload as Record<string, MetadataFieldEntry[]> | null | undefined,
    [],
  );

  if (!fields || fields.length === 0) return undefined;

  let bestScore = -Infinity;
  let bestValue = '';

  for (const field of fields) {
    // Skip the "name" slug — that's already checked earlier in the chain
    if (field.definition?.slug === FD_NAME_SLUG) continue;

    const { score, value } = scoreField(field);
    if (score > bestScore) {
      bestScore = score;
      bestValue = value;
    }
  }

  if (bestScore < MIN_SCORE_THRESHOLD || !bestValue) return undefined;

  // Truncate long values
  if (bestValue.length > MAX_DISPLAY_LENGTH) {
    return `${bestValue.substring(0, MAX_DISPLAY_LENGTH)}...`;
  }

  return bestValue;
}

/**
 * Clean a filename for display: strip extension, replace separators with spaces.
 */
function cleanFilename(filename: string): string {
  // Remove extension
  const lastDot = filename.lastIndexOf('.');
  const base = lastDot > 0 ? filename.substring(0, lastDot) : filename;

  // Replace underscores and hyphens with spaces
  return base.replace(/[_-]/g, ' ').trim();
}

/**
 * Check if a string is a meaningful non-empty value.
 */
function isPresent(val: string | null | undefined): val is string {
  return !!val && val.trim().length > 0;
}

/**
 * Check if a value looks like a system-generated ID rather than a human-readable name.
 * Catches standard UUIDs and alphanumeric UUID-like IDs (e.g. Elasticsearch document IDs).
 */
export function looksLikeId(val: string): boolean {
  return UUID_PATTERN.test(val) || ID_LIKE_PATTERN.test(val);
}

/** Common media file extensions — used to detect raw filenames passed as names */
const FILE_EXTENSION_PATTERN = /\.[a-zA-Z0-9]{2,5}$/;

/**
 * Check if a value looks like a raw filename (has a file extension).
 */
export function looksLikeFilename(val: string): boolean {
  return FILE_EXTENSION_PATTERN.test(val);
}

/**
 * Detect if a string looks like a raw filename (has a file extension).
 * If so, clean it for display (strip extension, replace separators).
 * Otherwise return it as-is.
 */
function cleanIfFilename(val: string): string {
  if (FILE_EXTENSION_PATTERN.test(val)) {
    return cleanFilename(val);
  }
  return val;
}

/**
 * Resolve the best display name for an asset using a prioritized fallback chain:
 *
 * 1. Explicit name (from enriched drag data or direct prop)
 * 2. contentObject.name (from GraphQL)
 * 3. Metadata "name" slug value
 * 4. Cleaned filename (extension stripped, separators replaced)
 * 5. Smart inference from all metadata fields (scoring algorithm)
 * 6. Truncated asset ID
 * 7. "Untitled Asset"
 */
export function getAssetDisplayName(options: AssetDisplayNameOptions): string {
  const { name, contentObjectName, metadataPayload, filename, id } = options;

  // 1. Explicit name (skip system-generated IDs, clean raw filenames)
  if (isPresent(name) && !looksLikeId(name)) return cleanIfFilename(name);

  // 2. contentObject.name from GraphQL (skip system-generated IDs, clean raw filenames)
  if (isPresent(contentObjectName) && !looksLikeId(contentObjectName)) return cleanIfFilename(contentObjectName);

  // 3. Metadata "name" slug
  if (metadataPayload) {
    const fields = getFirstNestedWithSaneDefault<MetadataFieldEntry[]>(
      metadataPayload as Record<string, MetadataFieldEntry[]> | null | undefined,
      [],
    );
    const nameField = fields?.find((mf) => mf.definition?.slug === FD_NAME_SLUG);
    if (nameField) {
      const nameValue = getFieldValue(nameField);
      if (isPresent(nameValue)) return nameValue;
    }
  }

  // 4. Cleaned filename
  if (isPresent(filename)) {
    const cleaned = cleanFilename(filename);
    if (isPresent(cleaned)) return cleaned;
  }

  // 5. Smart inference from all metadata
  const inferred = inferTitleFromMetadata(metadataPayload);
  if (inferred) return inferred;

  // 6. Truncated ID
  if (isPresent(id)) return `Asset ${id.substring(0, 8)}...`;

  // 7. Absolute last resort
  return 'Untitled Asset';
}
